import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  DialogActions,
  Box,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * סוגי מחיקה עבור מודל מחיקה
 */
export type DeleteType = 'school' | 'grade' | 'user';

/**
 * Props עבור DeleteConfirmationModal
 */
export interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: DeleteType;
  itemName: string;
  isLoading?: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  itemName,
  isLoading = false,
  cancelButtonText,
  confirmButtonText,
}) => {
  const { t } = useTranslation();

  const getTitle = () => {
    switch (type) {
      case 'school':
        return t('deleteSchoolTitle');
      case 'grade':
        return t('deleteGradeTitle');
      case 'user':
        return t('deleteUserTitle');
      default:
        return t('deleteTitle');
    }
  };

  const getMessage = () => {
    const message = (() => {
      switch (type) {
        case 'school':
          return t('deleteSchoolMessage', { itemName });
        case 'grade':
          return t('deleteGradeMessage', { itemName });
        case 'user':
          return t('deleteUserMessage', { itemName });
        default:
          return t('deleteMessage', { itemName });
      }
    })();
    
    return (
      <>
        {message}
        <br />
        {t('actionIrreversible')}
      </>
    );
  };

  const getWarningText = () => {
    switch (type) {
      case 'school':
        return t('deleteSchoolWarning');
      case 'grade':
        return t('deleteGradeWarning');
      case 'user':
        return t('deleteUserWarning');
      default:
        return t('deleteItemWarning');
    }
  };

  const getLoadingText = () => {
    switch (type) {
      case 'school':
        return t('deletingSchool');
      case 'grade':
        return t('deletingGrade');
      case 'user':
        return t('deletingUser');
      default:
        return t('deleting');
    }
  };

  const getIcon = () => {
    return <DeleteIcon className="delete-icon" sx={{ backgroundColor: "white", fontSize: 46, border: "6px solid #b689e3", borderRadius: "50%", path: { color: "#b689e3" } }}/>;
  };

  return (
    <Dialog open={isOpen} onClose={isLoading ? undefined : onClose} className="delete-confirmation-modal message-modal">
      <IconButton onClick={onClose} disabled={isLoading} className="close-button">
        <CloseIcon />
      </IconButton>

      <DialogTitle component="h3">
        {getIcon()}
        {getTitle()}
      </DialogTitle>

      <DialogContent>
        <p>{getMessage()}</p>

        <Box className="warning-box">
          <WarningIcon />
          <span>{getWarningText()}</span>
        </Box>
      </DialogContent>

      <DialogActions style={{ marginBottom: 0 }}>
        <Button onClick={onClose} variant="outlined" disabled={isLoading} className="cancel-button">
          {cancelButtonText || t('cancel')}
        </Button>
        <Button
          className="delete-button"
          onClick={onConfirm}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? t('deleting') : (confirmButtonText || t('delete'))}
        </Button>
      </DialogActions>

      {/* Loading Overlay */}
      {isLoading && (
        <Box className="loading-overlay">
          <CircularProgress size={60} />
          <h4>{getLoadingText()}</h4>
          <p>{t('pleaseWait')}</p>
        </Box>
      )}
    </Dialog>
  );
};

export default DeleteConfirmationModal;
